import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocaleStore, type Locale } from '@/i18n';
import { apiGet, apiPost } from './_base';
import { isFeature } from '@/clients';
import { parseImageHostRewrite, rewriteImageHost } from '@/lib/imageHost';

export type SlideDto = {
  id: string;
  image: string;
  device_type: string;
  click_action: string;
  click_action_params: string;
  color: string;
  top_up_req: string;
};

export type MarqueeDto = {
  id: string;
  text: string;
  click_action: string;
  click_action_params: string;
  created_at: number;
  updated_at: number;
};

export type NotificationDto = {
  id: string;
  popup_enabled: boolean;
  click_action: string;
  click_action_params: string;
  description: string;
  notification?: { title: string; content: string; jump_to_text: string; url: string };
  is_read: boolean;
  created_at: number;
  updated_at: number;
};

/**
 * A sign-in announcement from `/api/v1/ui-config/notification/login`.
 *
 * Deliberately NOT `NotificationDto`: a login notice has no per-user row, so
 * it has no `is_read` and cannot be marked read — sending its id to
 * `POST /api/v1/ui-config/notification` is refused with code 2172. Keeping the
 * field off the type makes that state unrepresentable rather than undefined.
 */
export type LoginNotification = {
  id: string;
  popup_enabled: boolean;
  click_action: string;
  click_action_params: string;
  /** Internal admin label — never rendered. */
  description: string;
  notification?: { title: string; content: string; jump_to_text: string; url: string };
  created_at: number;
  updated_at: number;
};

type LoginNotificationsResponse = { notifications: LoginNotification[] };

/**
 * Fetches the sign-in announcements. Call ONLY straight after a successful
 * login or register — never on resume, navigation, a timer or a refresh. The
 * response is identical between calls, so a second call can only re-show
 * something the user already dismissed.
 *
 * Deliberately a plain function rather than a hook: a mounted useQuery would
 * refetch on remount / invalidation / reconnect, which is exactly what the
 * endpoint's contract forbids.
 *
 * Notices are dropped unless `popup_enabled` — there is no inline surface at
 * sign-in for the rest to live on. Filtered here so a non-popup notice cannot
 * reach the UI by any later path.
 */
export async function fetchLoginNotifications(): Promise<LoginNotification[]> {
  const res = await apiGet<LoginNotificationsResponse>('/api/v1/ui-config/notification/login');
  return (res.notifications ?? []).filter((n) => n.popup_enabled);
}

type SlidesResponse = { slides: SlideDto[] };
type MarqueesResponse = { marquees: MarqueeDto[] };
type NotificationsResponse = { notifications: NotificationDto[] };

// locale lives in the queryKey because the backend localizes slide image
// URLs (multi-locale strings) and marquee text via Accept-Language. Caching
// per locale lets switching back be instant without a refetch.
/**
 * @param enabled Pass false to skip the request entirely. A client that ships
 *   a fixed banner (`home.banner`) has no use for the CMS's slides, and a
 *   disabled query is cheaper than one whose answer is discarded.
 */
export function useSlidesQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const locale = useLocaleStore((s) => s.locale);
  return useQuery({
    queryKey: ['ui', 'slides', locale],
    queryFn: () => apiGet<SlidesResponse>('/api/v1/ui-config/slide'),
    staleTime: 10 * 60_000, // banners change rarely — treat as fresh for 10 min
    enabled,
  });
}

export function useMarqueesQuery() {
  const locale = useLocaleStore((s) => s.locale);
  return useQuery({
    queryKey: ['ui', 'marquees', locale],
    queryFn: () => apiGet<MarqueesResponse>('/api/v1/ui-config/marquee'),
    staleTime: 10 * 60_000,
  });
}

export const NOTIFICATIONS_QUERY_KEY = ['ui', 'notifications'] as const;

// User-facing notifications. Backend tracks per-user read state via the POST
// endpoint; the `is_read` field on each item reflects that. Localized via
// Accept-Language by the server; keyed per-locale so switching is instant.
export function useNotificationsQuery(options?: { enabled?: boolean }) {
  const locale = useLocaleStore((s) => s.locale);
  return useQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, locale],
    queryFn: () => apiGet<NotificationsResponse>('/api/v1/ui-config/notification'),
    select: (res) => res.notifications ?? [],
    staleTime: 30_000,
    // A client with announcements switched off never requests them. Gated
    // here rather than at each call site so the bell badge, the popup and
    // the notifications page all go quiet together — there is no path left
    // that could fire the request.
    //
    // A caller may gate it further but never loosen it: the bottom nav only
    // wants the count when one of its tabs actually shows a badge, and a bar
    // without one should not make the request at all.
    enabled: isFeature('notifications') && (options?.enabled ?? true),
  });
}

// POST /api/v1/ui-config/notification — marks a single notification as read.
// Invalidates the notifications list so `is_read` reflects the updated state.
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notification_id: string) =>
      apiPost<void>('/api/v1/ui-config/notification', { notification_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
    },
  });
}

// Backend returns multi-locale strings like `en:https://... | zh:https://...`.
// Pick the URL for the current locale, then base language, then en, then any,
// then apply the client's image-host rewrite if it has one (see
// src/lib/imageHost.ts). Every cover and slide URL the backend serves comes
// through here, so this is the one place that rewrite has to live.
const IMAGE_HOST_REWRITE = parseImageHostRewrite(import.meta.env.VITE_IMAGE_HOST_REWRITE);

export function pickLocaleAsset(value: string, locale: Locale): string {
  return rewriteImageHost(pickLocaleUrl(value, locale), IMAGE_HOST_REWRITE);
}

function pickLocaleUrl(value: string, locale: Locale): string {
  if (!value) return '';
  // Plain URL (no locale prefix) — return as-is.
  if (!/^[a-z-]{2,5}:\s*https?:/i.test(value.trim())) return value;
  const map: Record<string, string> = {};
  for (const part of value.split('|')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const colonIdx = trimmed.indexOf(':');
    if (colonIdx <= 0) continue;
    const key = trimmed.slice(0, colonIdx).toLowerCase();
    const url = trimmed.slice(colonIdx + 1).trim();
    if (/^https?:\/\//i.test(url)) map[key] = url;
  }
  const lang = locale.toLowerCase().split('-')[0];
  return map[locale.toLowerCase()] || map[lang] || map['en'] || Object.values(map)[0] || '';
}
